import { Heart, Star, MapPin, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { OpenClosedBadge } from './OpenClosedBadge';
import { OpeningHours } from './OpeningHoursEditor';

interface ListingCardProps {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  image: string;
  rating?: number | null;
  location?: string;
  date?: string;
  time?: string;
  category?: string | null;
  linkPrefix: string;
  showFavorite?: boolean;
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string) => void;
  isLive?: boolean;
  isFeatured?: boolean;
  isFree?: boolean;
  isPromoted?: boolean;
  openingHours?: OpeningHours | null;
}

export const ListingCard = ({
  id,
  slug,
  title,
  subtitle,
  image,
  rating,
  location,
  date,
  time,
  category,
  linkPrefix,
  showFavorite = false,
  isFavorite = false,
  onFavoriteToggle,
  isLive = false,
  isFeatured = false,
  isFree = false,
  isPromoted = false,
  openingHours,
}: ListingCardProps) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    navigate(`${linkPrefix}/${slug}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFavoriteToggle?.(id);
  };

  return (
    <div 
      className="group cursor-pointer"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden rounded-xl mb-3">
        <div 
          className={cn(
            "absolute inset-0 bg-muted transition-opacity duration-300",
            imageLoaded ? "opacity-0" : "opacity-100"
          )}
        />
        <img
          src={image || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=800&fit=crop"}
          alt={title}
          className={cn(
            "w-full h-full object-cover transition-transform duration-500 ease-out",
            isHovered && "scale-105"
          )}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />
        {/* Vignette overlay for depth and polish */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-black/10 pointer-events-none rounded-xl" />
        
        {/* Favorite Button */}
        {showFavorite && (
          <button
            onClick={handleFavoriteClick}
            className="absolute top-3 right-3 p-2 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background transition-all duration-200 hover:scale-110"
          >
            <Heart 
              className={cn(
                "w-5 h-5 transition-all",
                isFavorite 
                  ? "fill-brand text-brand" 
                  : "text-foreground/70 hover:text-foreground"
              )} 
            />
          </button>
        )}

        {/* Free Badge - Bottom Left */}
        {isFree && (
          <div className="absolute bottom-3 left-3 px-3 py-1 bg-green-600 text-white rounded-full text-xs font-semibold shadow-lg">
            FREE
          </div>
        )}

        {/* Featured Badge - Bottom Right (priority over Open/Closed) */}
        {isFeatured && (
          <div className="absolute bottom-3 right-3 px-3 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg">
            <Crown className="w-3 h-3" />
            FEATURED
          </div>
        )}

        {/* Promoted Badge - Bottom Right (if not featured) */}
        {isPromoted && !isFeatured && (
          <div className="absolute bottom-3 right-3 px-3 py-1 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg">
            PROMOTED
          </div>
        )}

        {/* Open/Closed Badge - Bottom Right (only if not featured/promoted) */}
        {openingHours && !isFeatured && !isPromoted && (
          <OpenClosedBadge
            hours={openingHours}
            className="absolute bottom-3 right-3"
            size="sm"
          />
        )}

        {/* Live Badge */}
        {isLive && !isFeatured && !isPromoted && (
          <div className="absolute top-3 left-3 px-3 py-1 bg-brand text-brand-foreground rounded-full text-xs font-semibold">
            LIVE NOW
          </div>
        )}

        {/* Category Badge */}
        {category && !isLive && !isFeatured && (
          <div className="absolute top-3 left-3 px-3 py-1 bg-background/90 backdrop-blur-sm rounded-full text-xs font-medium">
            {category}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-1">
        {/* Title and Rating Row */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-foreground line-clamp-1">
            {title}
          </h3>
          {rating !== undefined && rating !== null && rating > 0 && (
            <div className="flex items-center gap-1 shrink-0">
              <Star className="w-4 h-4 fill-foreground text-foreground" />
              <span className="text-sm font-medium">{Number(rating).toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Subtitle / Location */}
        {(subtitle || location) && (
          <p className="text-muted-foreground text-sm line-clamp-1">
            {subtitle || location}
          </p>
        )}

        {/* Date and Time */}
        {(date || time) && (
          <p className="text-muted-foreground text-sm">
            {date}{date && time && ' · '}{time}
          </p>
        )}

      </div>
      </div>
    </div>
  );
};
